import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, Globe } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { useLanguage } from '../context/LanguageContext'
import SearchBar from './SearchBar'

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const { t, toggleLanguage, language } = useLanguage()
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/services', label: t('nav.services') },
    { path: '/portfolio', label: 'Work' },
    { path: '/contact', label: t('nav.contact') },
  ]

  const isActive = (path) => location.pathname === path

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-dark/95 backdrop-blur-lg shadow-gold border-b border-gold-500/20' 
          : 'bg-dark/80 backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="relative">
              <img 
                src="/logo.png" 
                alt="Contenta Logo" 
                className="w-12 h-12 object-contain transform group-hover:scale-110 transition-transform duration-300 drop-shadow-2xl"
              />
              <div className="absolute -inset-1 bg-gradient-gold rounded-full opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-300"></div>
            </div>
            <div className="hidden sm:block">
              <span className="text-2xl font-display font-bold text-gradient-gold">
                Contenta<span className="text-gold-600">.al</span>
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative font-medium transition-colors duration-300 ${
                  isActive(link.path)
                    ? 'text-gold-400'
                    : 'text-white hover:text-gold-400'
                }`}
              >
                {link.label}
                {isActive(link.path) && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-gold"
                    initial={false}
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                  />
                )}
              </Link>
            ))}
            
            {/* Search Bar */}
            <SearchBar />
            
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-2 px-4 py-2 rounded-full bg-bronze-900/50 hover:bg-bronze-900 transition-colors duration-300 text-white"
            >
              <Globe className="w-4 h-4 text-gold-600" />
              <span className="font-medium text-sm">{language.toUpperCase()}</span>
            </button>

            {/* CTA Buttons */}
            <Link to="/apply" className="btn-primary">
              Apliko për Projekt
            </Link>
            <Link to="/contact" className="btn-secondary">
              Kontakto
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-bronze-900/50 transition-colors duration-300"
          >
            {isOpen ? (
              <X className="w-6 h-6 text-gold-400" />
            ) : (
              <Menu className="w-6 h-6 text-gold-400" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-dark/98 backdrop-blur-lg border-t border-gold-500/20"
          >
            <div className="container mx-auto px-4 py-6 space-y-4">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`block py-3 px-4 rounded-lg font-medium transition-colors duration-300 ${
                      isActive(link.path)
                        ? 'bg-gradient-gold text-white'
                        : 'text-cream hover:bg-bronze-900/50'
                    }`}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
              
              <div className="space-y-4 pt-4 border-t border-gold-500/20">
                <button
                  onClick={toggleLanguage}
                  className="flex items-center justify-center space-x-2 w-full px-4 py-3 rounded-lg bg-bronze-900/50 hover:bg-bronze-900 transition-colors duration-300 text-white"
                >
                  <Globe className="w-4 h-4 text-gold-600" />
                  <span className="font-medium">{language === 'sq' ? 'Shqip' : 'English'}</span>
                </button>
                <Link
                  to="/apply"
                  onClick={() => setIsOpen(false)}
                  className="btn-primary w-full text-center block"
                >
                  Apliko për Projekt
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setIsOpen(false)}
                  className="btn-secondary w-full text-center block"
                >
                  Kontakto
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  )
}

export default Navbar
